import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, Spinner, Image } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function MarketPlacerAddEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = Boolean(id);
    const BASE_IMG_URL = process.env.REACT_APP_IMG_URL;

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        nickname: "",
        phone: "",
        password: "",
        password_confirmation: "",
        settlement_type: "",
        payment_type: "",
        payment_value: "",
        father_name: "",
        gender: "",
        dob: "",
        cnic: "",
        whatsapp: "",
        house_address: "",
        bank_name: "",
        bank_account_title: "",
        bank_account_number: "",
        gmail: "",
        gmail_password: "",
        ebay_username: "",
        ebay_password: "",
        payoneer_username: "",
        payoneer_password: "",
        paypal_username: "",
        paypal_password: "",
        reference: "",
        account_date: "",
        account_status: true,
        account_message: "",
        opening_balance: 0,
        opening_product_number: 0,
        opbs: 0,
        shipping_only: false,
        listing_limit: 0,
        remaining_limit: 0,
        dollar_limit: 0,
    });

    const [files, setFiles] = useState({
        cnic_front_image: null,
        cnic_back_image: null,
        atm_front_image: null,
        atm_back_image: null,
        bank_statement: null,
        user_image: null,
    });

    const [imagePreviews, setImagePreviews] = useState({
        user_image: null,
        cnic_front_image: null,
        cnic_back_image: null,
        atm_front_image: null,
        atm_back_image: null,
        bank_statement: null,
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [pageLoading, setPageLoading] = useState(isEdit && !location.state?.marketPlacer);
    const [alert, setAlert] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const getFullImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        const cleanBaseUrl = BASE_IMG_URL.endsWith("/") ? BASE_IMG_URL : `${BASE_IMG_URL}/`;
        const cleanPath = path.startsWith("/") ? path.substring(1) : path;
        return `${cleanBaseUrl}${cleanPath}`;
    };

    const populateForm = (item) => {
        const profile = item.mplacer || {};
        setFormData({
            name: item.name || "",
            email: item.email || "",
            nickname: item.nickname || "",
            phone: item.phone || "",
            password: "",
            password_confirmation: "",
            settlement_type: profile.settlement_type || item.settlement_type || "",
            payment_type: profile.payment_type || "",
            payment_value: profile.payment_value ?? "",
            father_name: profile.father_name || "",
            gender: profile.gender || "",
            dob: profile.dob || "",
            cnic: profile.cnic || "",
            whatsapp: profile.whatsapp || "",
            house_address: profile.house_address || "",
            bank_name: profile.bank_name || "",
            bank_account_title: profile.bank_account_title || "",
            bank_account_number: profile.bank_account_number || "",
            gmail: profile.gmail || "",
            gmail_password: profile.gmail_password || "",
            ebay_username: profile.ebay_username || "",
            ebay_password: profile.ebay_password || "",
            payoneer_username: profile.payoneer_username || "",
            payoneer_password: profile.payoneer_password || "",
            paypal_username: profile.paypal_username || "",
            paypal_password: profile.paypal_password || "",
            reference: profile.reference || "",
            account_date: profile.account_date || "",
            account_status: profile.account_status ?? true,
            account_message: profile.account_message || "",
            opening_balance: profile.opening_balance ?? 0,
            opening_product_number: profile.opening_product_number ?? 0,
            opbs: profile.opbs ?? 0,
            shipping_only: profile.shipping_only ?? false,
            listing_limit: profile.listing_limit ?? 0,
            remaining_limit: profile.remaining_limit ?? 0,
            dollar_limit: profile.dollar_limit ?? 0,
        });

        setImagePreviews({
            user_image: getFullImageUrl(profile.user_image),
            cnic_front_image: getFullImageUrl(profile.cnic_front_image),
            cnic_back_image: getFullImageUrl(profile.cnic_back_image),
            atm_front_image: getFullImageUrl(profile.atm_front_image),
            atm_back_image: getFullImageUrl(profile.atm_back_image),
            bank_statement: getFullImageUrl(profile.bank_statement),
        });
    };

    useEffect(() => {
        if (isEdit) {
            if (location.state?.marketPlacer) {
                populateForm(location.state.marketPlacer);
                setPageLoading(false);
            } else {
                const fetchMarketPlacer = async () => {
                    try {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_URL}/admin/market-placer`,
                            { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
                        );
                        if (res.data?.success) {
                            const item = res.data.data.find((m) => m.id === parseInt(id));
                            if (item) {
                                populateForm(item);
                            } else {
                                setAlert({ type: "danger", message: "Market Placer not found." });
                            }
                        }
                    } catch (err) {
                        setAlert({ type: "danger", message: "Failed to load details." });
                    } finally {
                        setPageLoading(false);
                    }
                };
                fetchMarketPlacer();
            }
        }
    }, [id, isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            const file = selectedFiles[0];
            setFiles((prev) => ({ ...prev, [name]: file }));
            setImagePreviews((prev) => ({ ...prev, [name]: URL.createObjectURL(file) }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        const payload = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== null && formData[key] !== "") {
                payload.append(key, typeof formData[key] === "boolean" ? (formData[key] ? "1" : "0") : formData[key]);
            }
        });

        Object.keys(files).forEach((key) => {
            if (files[key]) payload.append(key, files[key]);
        });

        let url = `${process.env.REACT_APP_API_URL}/admin/market-placer`;
        if (isEdit) {
            url = `${process.env.REACT_APP_API_URL}/admin/market-placer/${id}`;
            payload.append("_method", "PUT");
        }

        try {
            const response = await axios.post(url, payload, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.data?.success) {
                navigate("/market-placer");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const renderDocumentField = (label, name, accept = "image/*") => (
        <Col md={4} key={name}>
            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-secondary">{label}</Form.Label>
                <Form.Control type="file" name={name} onChange={handleFileChange} accept={accept} className="shadow-none border-1" />
                {errors[name] && <div className="text-danger small mt-1">{errors[name][0]}</div>}
                {imagePreviews[name] && (
                    <div className="mt-2 text-center p-2 border rounded-3 bg-light shadow-sm">
                        {imagePreviews[name].toLowerCase().endsWith(".pdf") ? (
                            <a href={imagePreviews[name]} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                                {t("View PDF Document")}
                            </a>
                        ) : (
                            <Image
                                src={imagePreviews[name]}
                                alt={label}
                                thumbnail
                                className="rounded-2"
                                style={{ maxHeight: "140px", objectFit: "contain", width: "100%" }}
                            />
                        )}
                    </div>
                )}
            </Form.Group>
        </Col>
    );

    if (pageLoading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted fw-semibold">{t("Loading data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4 p-3 bg-white rounded-3 shadow-sm border-0">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title fw-bold text-dark mb-1">
                                    {isEdit ? t("Edit Market Placer") : t("Add Market Placer")}
                                </h3>
                                <ul className="mc-breadcrumb-list list-inline mb-0 text-muted small">
                                    <li className="list-inline-item">
                                        <Link to="#" className="text-decoration-none text-secondary">{t("Home")}</Link>
                                    </li>
                                    <li className="list-inline-item">/</li>
                                    <li className="list-inline-item">
                                        <Link to="/admin/market-placer" className="text-decoration-none text-secondary">{t("Market Placers")}</Link>
                                    </li>
                                    <li className="list-inline-item">/</li>
                                    <li className="list-inline-item text-primary fw-medium">{isEdit ? t("Edit") : t("Add")}</li>
                                </ul>
                            </div>
                            <Button variant="secondary" size="sm" className="px-3 rounded-pill" onClick={() => navigate("/market-placer")}>
                                {t("Back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                        <Card.Header className="bg-white py-3 border-bottom">
                            <h5 className="mb-0 fw-bold text-dark">{t("Account Details")}</h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="rounded-3 shadow-sm">
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Full Name *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                placeholder="e.g. John Doe"
                                                value={formData.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                                required
                                                className="shadow-none"
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.name?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Nickname *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nickname"
                                                placeholder="e.g. Johnny"
                                                value={formData.nickname}
                                                onChange={handleChange}
                                                isInvalid={!!errors.nickname}
                                                required
                                                className="shadow-none"
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.nickname?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Email Address *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="e.g. john@example.com"
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                required
                                                className="shadow-none"
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.email?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Phone Number *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                placeholder="e.g. 1234567890"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                isInvalid={!!errors.phone}
                                                required
                                                className="shadow-none"
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.phone?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Settlement Type *</Form.Label>
                                            <Form.Select
                                                name="settlement_type"
                                                value={formData.settlement_type}
                                                onChange={handleChange}
                                                isInvalid={!!errors.settlement_type}
                                                className="shadow-none"
                                            >
                                                <option value="">Select Settlement Type</option>
                                                <option value="receivable">Receivable</option>
                                                <option value="payable">Payable</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.settlement_type?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Payment Type</Form.Label>
                                            <Form.Select
                                                name="payment_type"
                                                value={formData.payment_type}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_type}
                                                className="shadow-none"
                                            >
                                                <option value="">Select</option>
                                                <option value="percentage">Percentage</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.payment_type?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Commision Percentage</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="payment_value"
                                                placeholder="Percentage"
                                                value={formData.payment_value}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_value}
                                                className="shadow-none"
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.payment_value?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">{isEdit ? "New Password (Optional)" : "Password *"}</Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    placeholder="Enter password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.password}
                                                    required={!isEdit}
                                                    className="shadow-none pe-5"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="position-absolute top-50 translate-middle-y end-0 me-2 p-0 text-secondary text-decoration-none"
                                                    style={{ transform: "translateY(-50%)" }}
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                >
                                                    {showPassword ? "Hide" : "Show"}
                                                </Button>
                                            </div>
                                            <Form.Control.Feedback type="invalid">{errors.password?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">{isEdit ? "Confirm New Password" : "Confirm Password *"}</Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="password_confirmation"
                                                    placeholder="Re-enter password"
                                                    value={formData.password_confirmation}
                                                    onChange={handleChange}
                                                    required={!isEdit || Boolean(formData.password)}
                                                    className="shadow-none pe-5"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="position-absolute top-50 translate-middle-y end-0 me-2 p-0 text-secondary text-decoration-none"
                                                    style={{ transform: "translateY(-50%)" }}
                                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                                >
                                                    {showConfirmPassword ? "Hide" : "Show"}
                                                </Button>
                                            </div>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3 border-secondary-subtle" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary mb-3">{t("Profile Information")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Father Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="father_name"
                                                placeholder="e.g. Richard Doe"
                                                value={formData.father_name}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Gender</Form.Label>
                                            <Form.Select name="gender" value={formData.gender} onChange={handleChange} className="shadow-none">
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Date of Birth</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="dob"
                                                value={formData.dob}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">CNIC</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="cnic"
                                                placeholder="e.g. 35201-XXXXXXX-X"
                                                value={formData.cnic}
                                                onChange={handleChange}
                                                isInvalid={!!errors.cnic}
                                                className="shadow-none"
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.cnic?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">WhatsApp Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="whatsapp"
                                                placeholder="e.g. +1234567890"
                                                value={formData.whatsapp}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Gmail</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="gmail"
                                                placeholder="e.g. account@gmail.com"
                                                value={formData.gmail}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Gmail Password</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="gmail_password"
                                                placeholder="Enter Gmail password"
                                                value={formData.gmail_password}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">House Address</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="house_address"
                                                placeholder="e.g. Street #1, House #2, City, Country"
                                                value={formData.house_address}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3 border-secondary-subtle" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary mb-3">{t("Banking & Marketplace Accounts")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Bank Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_name"
                                                placeholder="e.g. Chase Bank"
                                                value={formData.bank_name}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Bank Account Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_account_title"
                                                placeholder="e.g. John Doe"
                                                value={formData.bank_account_title}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Bank Account Number / IBAN</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_account_number"
                                                placeholder="e.g. US12345678901234"
                                                value={formData.bank_account_number}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">eBay Username</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="ebay_username"
                                                placeholder="e.g. ebay_seller_123"
                                                value={formData.ebay_username}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">eBay Password</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="ebay_password"
                                                placeholder="Enter eBay password"
                                                value={formData.ebay_password}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Payoneer Username / ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="payoneer_username"
                                                placeholder="e.g. payoneer_user"
                                                value={formData.payoneer_username}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Payoneer Password</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="payoneer_password"
                                                placeholder="Enter Payoneer password"
                                                value={formData.payoneer_password}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">PayPal Username / Email</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="paypal_username"
                                                placeholder="e.g. paypal@example.com"
                                                value={formData.paypal_username}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">PayPal Password</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="paypal_password"
                                                placeholder="Enter PayPal password"
                                                value={formData.paypal_password}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3 border-secondary-subtle" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary mb-3">{t("Limits & Balances Configuration")}</h6></Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Opening Balance</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="opening_balance"
                                                placeholder="e.g. 1000.00"
                                                value={formData.opening_balance}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Opening Product Number</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="1"
                                                name="opening_product_number"
                                                placeholder="e.g. 50"
                                                value={formData.opening_product_number}
                                                onChange={handleChange}
                                                isInvalid={!!errors.opening_product_number}
                                                className="shadow-none"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.opening_product_number?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Listing Limit</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="listing_limit"
                                                placeholder="e.g. 500"
                                                value={formData.listing_limit}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Remaining Limit</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="remaining_limit"
                                                placeholder="e.g. 200"
                                                value={formData.remaining_limit}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-secondary">Dollar Limit ($)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="dollar_limit"
                                                placeholder="e.g. 10000.00"
                                                value={formData.dollar_limit}
                                                onChange={handleChange}
                                                className="shadow-none"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3 border-secondary-subtle" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary mb-3">{t("Document Attachments")}</h6></Col>

                                    {renderDocumentField("User Image", "user_image")}
                                    {renderDocumentField("CNIC Front Image", "cnic_front_image")}
                                    {renderDocumentField("CNIC Back Image", "cnic_back_image")}
                                    {renderDocumentField("ATM Front Image", "atm_front_image")}
                                    {renderDocumentField("ATM Back Image", "atm_back_image")}
                                    {renderDocumentField("Bank Statement Document", "bank_statement", "image/*,application/pdf")}
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4 pt-3 border-top">
                                    <Button variant="secondary" className="px-4 border rounded-2" onClick={() => navigate("/market-placer")}>
                                        {t("Cancel")}
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={submitting} className="px-4 rounded-2">
                                        {submitting ? t("Saving...") : isEdit ? t("Update Market Placer") : t("Save Market Placer")}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}